import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { useToast } from '../../../shared/components/Toast';
import { useCriarCustodian, useCustodianEntityOptions, useCustodianTypeOptions, useLookupUsers } from '../hooks';
import type { Custodian } from '../api';
import { maskPhone } from '../../../shared/utils/validators';
import {
  mensagemSelecaoObrigatoria,
  montarCustodianPayload,
  origemDoTipo,
  rotuloDoAlvo,
  textoAjudaDoAlvo,
} from './custodiaCadastro';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (custodian: Custodian) => void;
}

interface FormState {
  type: string;
  /** `userId`, `departmentId` ou `clientId` conforme a origem do tipo. */
  selecionadoId: string;
  email: string;
  phone: string;
  jobTitle: string;
}

const EMPTY: FormState = {
  type: '',
  selecionadoId: '',
  email: '',
  phone: '',
  jobTitle: '',
};

/**
 * Cadastro de responsável (custodiário).
 *
 * O campo "Tipo" lista todos os perfis ativos parametrizados em `roles` e
 * também as entidades não pessoais que podem responder por ativos:
 * Departamento (áreas organizacionais) e Cliente — RN01 da #272/#541.
 * A origem do tipo escolhido decide o combo seguinte:
 *
 * - usuário → `/users/lookup` filtrado pelo role, com autopreenchimento de
 *   e-mail/telefone;
 * - departamento → áreas ativas (`/custodians/entity-options?type=department`);
 * - cliente → clientes ativos (`/custodians/entity-options?type=client`).
 *
 * Trocar o Tipo limpa a seleção (RN07) e recarrega as opções. O backend
 * deriva o `type` do custodiário a partir da origem enviada.
 */
export function CustodianCreateModal({ open, onClose, onCreated }: Props) {
  const mutation = useCriarCustodian();
  const toast = useToast();

  const typesQuery = useCustodianTypeOptions();
  const types = typesQuery.data ?? [];

  const [form, setForm] = useState<FormState>(EMPTY);

  const origin = origemDoTipo(types, form.type);
  const isUserOrigin = origin === 'user';
  const entityOrigin = origin === 'department' || origin === 'client' ? origin : undefined;

  const usersQuery = useLookupUsers(
    isUserOrigin && form.type ? [form.type] : [],
    isUserOrigin && !!form.type,
  );
  const entitiesQuery = useCustodianEntityOptions(entityOrigin);

  const users = usersQuery.data?.data ?? [];
  const entities = entitiesQuery.data ?? [];

  const typeOptions = useMemo(
    () => types.map((type) => ({ value: type.slug, label: type.name })),
    [types],
  );

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.name,
        // E-mail vai como texto secundário abaixo do nome — hierarquia
        // visual pedida pela PO. Selecionado, o input mostra só o nome.
        description: u.email,
      })),
    [users],
  );

  const entityOptions = useMemo(
    () =>
      entities.map((entity) => ({
        value: entity.id,
        label: entity.name,
        description: entity.description ?? undefined,
      })),
    [entities],
  );

  const options = isUserOrigin ? userOptions : entityOptions;
  const carregando = isUserOrigin ? usersQuery.isLoading : entitiesQuery.isLoading;

  // Se a seleção atual sumir da lista após mudar de Tipo, limpa.
  useEffect(() => {
    if (!form.selecionadoId) return;
    if (options.some((option) => option.value === form.selecionadoId)) return;
    setForm((prev) => ({
      ...prev,
      selecionadoId: '',
      ...(isUserOrigin ? { email: '', phone: '' } : {}),
    }));
  }, [options, form.selecionadoId, isUserOrigin]);

  function reset() {
    setForm(EMPTY);
  }

  function handleTypeChange(newType: string) {
    // RN07: trocar Tipo limpa seleção e campos auto-preenchidos.
    setForm({ ...EMPTY, type: newType, jobTitle: form.jobTitle });
  }

  function handleSelect(newId: string) {
    if (!isUserOrigin) {
      setForm((prev) => ({ ...prev, selecionadoId: newId }));
      return;
    }
    const user = users.find((u) => u.id === newId);
    setForm((prev) => ({
      ...prev,
      selecionadoId: newId,
      email: user?.email ?? prev.email,
      phone: user?.phone ?? prev.phone,
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!origin) {
      toast.warning('Selecione o Tipo');
      return;
    }
    if (!form.selecionadoId) {
      toast.warning(mensagemSelecaoObrigatoria(origin));
      return;
    }

    const payload = montarCustodianPayload({
      origin,
      selecionadoId: form.selecionadoId,
      email: form.email,
      phone: form.phone,
      jobTitle: form.jobTitle,
    });

    try {
      const created = await mutation.mutateAsync(payload);
      toast.success(`Custodiário "${created.name}" cadastrado.`);
      reset();
      onCreated(created);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao criar responsável'));
    }
  }

  const inputCls = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">Cadastrar responsável</Dialog.Title>

          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <AutocompleteSelect
                label="Tipo *"
                options={typeOptions}
                value={form.type}
                onChange={handleTypeChange}
                disabled={typesQuery.isLoading}
              />
              {typesQuery.isError && (
                <p className="mt-1 text-xs text-red-600">Não foi possível carregar os tipos.</p>
              )}
            </div>

            <div>
              <AutocompleteSelect
                label={origin ? rotuloDoAlvo(origin) : 'Usuário do Nexos *'}
                options={options}
                value={form.selecionadoId}
                onChange={handleSelect}
                disabled={!origin || carregando}
              />
              <p className="mt-1 text-xs text-gray-500">
                {!origin
                  ? 'Selecione um Tipo primeiro.'
                  : textoAjudaDoAlvo(origin, { carregando, quantidade: options.length })}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">E-mail</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Telefone</span>
                <input
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: maskPhone(e.target.value) }))}
                  className={inputCls}
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Cargo/função</span>
              <input
                value={form.jobTitle}
                onChange={(e) => setForm((p) => ({ ...p, jobTitle: e.target.value }))}
                className={inputCls}
              />
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { reset(); onClose(); }}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {mutation.isPending ? 'Cadastrando...' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
